import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RenderedSavedReply, SavedReply, SavedReplyInput } from '@peles/shared';
import {
  createSavedReply,
  deleteSavedReply,
  listSavedReplies,
  renderSavedReply,
  updateSavedReply,
  type MergeContext,
} from '../api/savedReplies';
import { useAuth } from '../context/AuthContext';

export const SAVED_REPLIES_KEY = ['saved-replies'] as const;

/**
 * The caller's own saved replies and the office's shared ones.
 *
 * ⚠️ **Keyed by who is asking.** The list holds personal replies, and an
 * administrator impersonating a producer switches user without a reload — a
 * shared key would show the administrator's private replies as the producer's
 * until it went stale. Invalidating by `SAVED_REPLIES_KEY` still reaches every
 * user's entry, since it is the prefix.
 */
export function useSavedReplies(enabled = true) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  return useQuery({
    queryKey: [...SAVED_REPLIES_KEY, userId],
    queryFn: listSavedReplies,
    enabled: enabled && userId !== null,
    staleTime: 60 * 1000,
  });
}

export function useCreateSavedReply() {
  const queryClient = useQueryClient();
  return useMutation<SavedReply, Error, SavedReplyInput>({
    mutationFn: createSavedReply,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SAVED_REPLIES_KEY }),
  });
}

export function useUpdateSavedReply() {
  const queryClient = useQueryClient();
  return useMutation<SavedReply, Error, { id: string; input: Partial<SavedReplyInput> }>({
    mutationFn: ({ id, input }) => updateSavedReply(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SAVED_REPLIES_KEY }),
  });
}

export function useDeleteSavedReply() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteSavedReply,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SAVED_REPLIES_KEY }),
  });
}

/** Fill a reply against the record the message is about. Not cached: the record changes. */
export function useRenderSavedReply() {
  return useMutation<RenderedSavedReply, Error, { id: string; record?: MergeContext }>({
    mutationFn: ({ id, record }) => renderSavedReply(id, record),
  });
}
